import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  AlertTitle,
  Breadcrumbs,
  Link,
  Divider,
  InputAdornment,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import { trpc } from '../../lib/trpc';

/**
 * Color picker component
 */
function ColorPicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (color: string) => void;
  label: string;
}) {
  const presetColors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#8B5CF6', // Purple
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#6366F1', // Indigo
    '#84CC16', // Lime
    '#F97316', // Orange
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {presetColors.map((color) => (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              backgroundColor: color,
              cursor: 'pointer',
              border: value === color ? 3 : 1,
              borderColor: value === color ? 'primary.main' : 'divider',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#3B82F6"
          sx={{ width: 120 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 0.5,
                    backgroundColor: value,
                    border: 1,
                    borderColor: 'divider',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Custom hex color
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Loading skeleton for the form
 */
function FormSkeleton() {
  return (
    <Paper sx={{ p: 4, maxWidth: 800 }}>
      <Skeleton variant="text" width="30%" height={32} sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
        <Divider sx={{ my: 2 }} />
        <Skeleton variant="text" width="20%" height={32} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        <Divider sx={{ my: 2 }} />
        <Skeleton variant="text" width="20%" height={32} />
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
      </Box>
    </Paper>
  );
}

/**
 * Not found component
 */
function StatusPageNotFound({ id }: { id: string }) {
  return (
    <Paper sx={{ p: 4, maxWidth: 800, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Status Page Not Found
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        The status page with ID &quot;{id}&quot; could not be found. It may have been deleted or you
        may not have permission to access it.
      </Typography>
      <Button
        component={RouterLink}
        to="/status-pages"
        variant="contained"
        startIcon={<ArrowBackIcon />}
      >
        Back to Status Pages
      </Button>
    </Paper>
  );
}

interface SelectedMonitor {
  monitorId: string;
  displayName: string;
  order: number;
}

/**
 * Edit status page component
 * Form for editing an existing status page
 */
export function EditStatusPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedMonitors, setSelectedMonitors] = useState<SelectedMonitor[]>([]);
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [isInitialized, setIsInitialized] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Fetch status page data
  const statusPageQuery = trpc.statusPages.getById.useQuery(
    { id: id! },
    {
      enabled: !!id,
      retry: false,
    }
  );

  // Fetch user's monitors
  const monitorsQuery = trpc.monitors.list.useQuery();

  // Update mutation
  const utils = trpc.useUtils();
  const updateMutation = trpc.statusPages.update.useMutation({
    onSuccess: (data) => {
      utils.statusPages.list.invalidate();
      utils.statusPages.getById.invalidate({ id: data.id });
      navigate(`/status-pages/${data.id}`, {
        state: { message: 'Status page updated successfully!' },
      });
    },
    onError: (error) => {
      setErrors((prev) => ({ ...prev, form: error.message }));
    },
  });

  // Reorder monitors mutation
  const reorderMutation = trpc.statusPages.reorderMonitors.useMutation();

  // Initialize form with fetched data
  useEffect(() => {
    if (statusPageQuery.data && !isInitialized) {
      setName(statusPageQuery.data.name);
      setDescription(statusPageQuery.data.description || '');
      setIsPublic(statusPageQuery.data.isPublic);
      setLogoUrl(statusPageQuery.data.branding?.logoUrl || '');
      setPrimaryColor(statusPageQuery.data.branding?.primaryColor || '#3B82F6');
      setSelectedMonitors(
        statusPageQuery.data.monitors.map((m) => ({
          monitorId: m.monitorId,
          displayName: m.displayName,
          order: m.order,
        }))
      );
      setIsInitialized(true);
    }
  }, [statusPageQuery.data, isInitialized]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (description && description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (logoUrl && !/^https?:\/\/.+/.test(logoUrl)) {
      newErrors.logoUrl = 'Logo URL must be a valid URL';
    }

    if (primaryColor && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(primaryColor)) {
      newErrors.primaryColor = 'Primary color must be a valid hex color';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !id) {
      return;
    }

    updateMutation.mutate({
      id,
      data: {
        name: name.trim(),
        description: description.trim() || null,
        isPublic,
        branding: {
          logoUrl: logoUrl.trim() || null,
          primaryColor: primaryColor || undefined,
        },
      },
    });
  };

  // Toggle monitor selection
  const toggleMonitor = (monitorId: string) => {
    setSelectedMonitors((prev) => {
      if (prev.some((m) => m.monitorId === monitorId)) {
        return prev.filter((m) => m.monitorId !== monitorId);
      }
      const monitor = monitorsQuery.data?.find((m) => m.id === monitorId);
      return [
        ...prev,
        {
          monitorId,
          displayName: monitor?.name || '',
          order: prev.length,
        },
      ];
    });
  };

  // Remove monitor from selection
  const removeMonitor = (monitorId: string) => {
    setSelectedMonitors((prev) =>
      prev.filter((m) => m.monitorId !== monitorId).map((m, index) => ({ ...m, order: index }))
    );
  };

  // Handle drag start
  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) return;

      setSelectedMonitors((prev) => {
        const newList = [...prev];
        const draggedItem = newList[draggedIndex];
        if (!draggedItem) return prev;
        newList.splice(draggedIndex, 1);
        newList.splice(index, 0, draggedItem);
        return newList.map((m, i) => ({ ...m, order: i }));
      });
      setDraggedIndex(index);
    },
    [draggedIndex]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    // Save reorder to server if we have an id
    if (id && selectedMonitors.length > 0) {
      reorderMutation.mutate({
        pageId: id,
        monitorIds: selectedMonitors.map((m) => m.monitorId),
      });
    }
  }, [id, selectedMonitors, reorderMutation]);

  // Get monitor name by ID
  const getMonitorName = (monitorId: string) => {
    return monitorsQuery.data?.find((m) => m.id === monitorId)?.name || 'Unknown Monitor';
  };

  // Get monitor status by ID
  const getMonitorStatus = (monitorId: string) => {
    return monitorsQuery.data?.find((m) => m.id === monitorId)?.status || 'PENDING';
  };

  // Show loading state
  if (statusPageQuery.isLoading) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Skeleton variant="text" width={100} />
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>

        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 4 }} />

        <FormSkeleton />
      </Box>
    );
  }

  // Show error state
  if (statusPageQuery.error) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Typography color="text.primary">Edit Status Page</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Edit Status Page
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Update status page settings
        </Typography>

        {statusPageQuery.error.data?.code === 'NOT_FOUND' ? (
          <StatusPageNotFound id={id || 'unknown'} />
        ) : (
          <Alert severity="error" sx={{ maxWidth: 800 }}>
            Failed to load status page: {statusPageQuery.error.message}
          </Alert>
        )}
      </Box>
    );
  }

  // Show not found if no data
  if (!statusPageQuery.data) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Typography color="text.primary">Edit Status Page</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Edit Status Page
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Update status page settings
        </Typography>

        <StatusPageNotFound id={id || 'unknown'} />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
          Status Pages
        </Link>
        <Link component={RouterLink} to={`/status-pages/${id}`} color="inherit" underline="hover">
          {statusPageQuery.data.name}
        </Link>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Edit Status Page
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Update settings for &quot;{statusPageQuery.data.name}&quot;
      </Typography>

      {/* Error Alert */}
      {updateMutation.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error updating status page</AlertTitle>
          {errors.form || updateMutation.error.message}
        </Alert>
      )}

      <Paper sx={{ p: 4, maxWidth: 800, mb: 3 }}>
        {/* Basic Info Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Basic Information
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Name */}
          <TextField
            label="Page Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'The name displayed on your status page'}
            required
            fullWidth
          />

          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Public URL
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {window.location.origin}/app/status/{statusPageQuery.data.slug}
            </Typography>
          </Box>

          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!errors.description}
            helperText={errors.description || 'Optional description shown on the status page'}
            multiline
            rows={3}
            fullWidth
          />

          {/* Public Toggle */}
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body1">Public Status Page</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isPublic
                    ? 'Anyone with the link can view this status page'
                    : 'This status page is private and only visible to you'}
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Monitors Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Monitors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag to reorder monitors. The order shown here is how they appear on the public page.
        </Typography>

        {/* Selected Monitors (Reorderable) */}
        {selectedMonitors.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <List dense disablePadding>
              {selectedMonitors.map((monitor, index) => (
                <ListItem
                  key={monitor.monitorId}
                  disablePadding
                  divider={index < selectedMonitors.length - 1}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    backgroundColor: draggedIndex === index ? 'action.selected' : 'transparent',
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, pl: 1 }}>
                    <DragIndicatorIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={getMonitorName(monitor.monitorId)}
                    secondary={`Order: ${index + 1}`}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Chip
                    label={getMonitorStatus(monitor.monitorId)}
                    size="small"
                    color={
                      getMonitorStatus(monitor.monitorId) === 'UP'
                        ? 'success'
                        : getMonitorStatus(monitor.monitorId) === 'DOWN'
                          ? 'error'
                          : 'default'
                    }
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <Tooltip title="Remove from status page">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeMonitor(monitor.monitorId)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Available Monitors (Add) */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add monitors:
        </Typography>

        {monitorsQuery.isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
        ) : monitorsQuery.data && monitorsQuery.data.length > 0 ? (
          <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
            <List dense disablePadding>
              {monitorsQuery.data
                .filter((monitor) => !selectedMonitors.some((m) => m.monitorId === monitor.id))
                .map((monitor) => (
                  <ListItem key={monitor.id} disablePadding divider>
                    <ListItemButton onClick={() => toggleMonitor(monitor.id)}>
                      <ListItemIcon>
                        <Checkbox edge="start" checked={false} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText
                        primary={monitor.name}
                        secondary={monitor.url}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{
                          noWrap: true,
                          sx: { maxWidth: 400 },
                        }}
                      />
                      <Chip
                        label={monitor.status}
                        size="small"
                        color={
                          monitor.status === 'UP'
                            ? 'success'
                            : monitor.status === 'DOWN'
                              ? 'error'
                              : 'default'
                        }
                        variant="outlined"
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{ p: 3, textAlign: 'center', backgroundColor: 'action.hover' }}
          >
            <MonitorHeartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No monitors available. Create monitors first to add them to your status page.
            </Typography>
            <Button
              component={RouterLink}
              to="/monitors/new"
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
            >
              Create Monitor
            </Button>
          </Paper>
        )}

        {selectedMonitors.length > 0 && (
          <FormHelperText sx={{ mt: 1 }}>
            {selectedMonitors.length} monitor{selectedMonitors.length !== 1 ? 's' : ''} on this
            status page
          </FormHelperText>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Branding Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Branding
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize the look of your status page
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Logo URL */}
          <TextField
            label="Logo URL"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            error={!!errors.logoUrl}
            helperText={errors.logoUrl || 'URL to your logo image (recommended: 200x50px)'}
            placeholder="https://example.com/logo.png"
            fullWidth
          />

          {/* Primary Color */}
          <ColorPicker value={primaryColor} onChange={setPrimaryColor} label="Primary Color" />
          {errors.primaryColor && <FormHelperText error>{errors.primaryColor}</FormHelperText>}
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          component={RouterLink}
          to={`/status-pages/${id}`}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={updateMutation.isLoading}
        >
          {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}

export default EditStatusPage;
